import { CalendarToday, Inventory2Outlined, ViewList } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Chip, CircularProgress, LinearProgress, Theme, Typography, useTheme } from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { myAppointmentsLink } from '../../../library/constants';
import { formatRecordDateTime } from '../../../library/dateTimeHelper';
import { IAppointment } from '../../../library/models/appointment';
import { IPatientPurchase, sessionsUsed } from '../../../library/models/patientPurchase';
import { useReactInfiniteQuery, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { activePlanChipStyles, activePlanHeaderRowStyles, activePlanProgressBarStyles, activePlanRemainingStyles, activePlanRowStyles, activePlanScheduledStyles, activePlanServiceNameStyles, activePlansListStyles, activePlanTherapistStyles, calendarIconStyles, cardMarginBottom, dashboardWrapperStyles, emptyStateBoxStyles, headerBoxStyles, leftColumnStyles, loadingBoxStyles, planIconStyles, quickActionCardStyles, quickActionIconStyle, quickActionIconTileStyles, quickActionInnerStyles, quickActionsGridStyles, sectionTitleStyles, subtitleStyles, titleStyles, upcomingHeaderBoxStyles, upcomingListStyles, upcomingRowStyles, upcomingStatusChipStyles, upcomingWhenStyles, upcomingWithStyles } from './dashboard.styles';
import ResourcesIds from '../../../resources/ResourcesIds';
import { localised } from '../../../library/models/localisedText';

/** How many upcoming appointments the card shows before pointing at My Appointments instead. */
const UPCOMING_LIMIT = 3;

export const Dashboard = (): JSX.Element => {
    const theme: Theme = useTheme();
    const navigate: NavigateFunction = useNavigate();
    const { user } = useAuth();
    const { t: translate, i18n } = useTranslation();

    /*
     * The first page of the patient's upcoming history, sized to what the card draws -- the
     * split between upcoming and past, the order and the count are all the server's. It used
     * to fetch six months of the diary and filter it here, comparing a UTC `toISOString()`
     * against the clinic's zone-less wall clock and keeping only SCHEDULED rows: paging
     * undone, a boundary off by the viewer's offset, and a booking request left off the card
     * entirely although it is the thing a patient most wants to see next.
     */
    const { data, isLoading } = useReactInfiniteQuery<IAppointment>(
        services.appointments.getAppointmentHistory(UPCOMING_LIMIT, { scope: 'UPCOMING' })
    );

    const upcomingAppointments: IAppointment[] = data?.pages[0]?.items ?? [];
    const upcomingTotal: number = data?.pages[0]?.totalItems ?? 0;

    // Same call staff use for the clinic's purchases -- the server tells a patient from
    // staff by role and hands back only their own, with the money and any notes left out.
    const { data: purchasesData, isLoading: isLoadingPurchases } = useReactQuery<IPatientPurchase[]>(
        services.patientPurchases.getPurchases()
    );

    const activePlans: IPatientPurchase[] = (purchasesData ?? []).filter(
        purchase => purchase.status === 'ACTIVE'
    );

    const QUICK_ACTIONS = [
        {
            title: translate(ResourcesIds.QUICK_ACTION_BOOK_NEW_APPOINTMENT_TITLE),
            description: translate(ResourcesIds.QUICK_ACTION_BOOK_NEW_APPOINTMENT_DESC),
            route: '/appointments',
            colorKey: 'primary',
        },
        {
            title: translate(ResourcesIds.QUICK_ACTION_VIEW_ALL_APPOINTMENTS_TITLE),
            description: translate(ResourcesIds.QUICK_ACTION_VIEW_ALL_APPOINTMENTS_DESC),
            route: '/my-appointments',
            colorKey: 'secondary',
        },
    ];

    return (
        <>
            <Box sx={headerBoxStyles}>
                <Typography variant="h4" component="h1" gutterBottom sx={titleStyles}>


                    {translate(ResourcesIds.WELCOME_USER, { name: user?.firstName || user?.emailAddress })}
                </Typography>
                <Typography variant="h6" sx={subtitleStyles}>
                    {translate(ResourcesIds.DASHBOARD_SUBTITLE)}
                </Typography>
            </Box>

            <Box sx={dashboardWrapperStyles}>
                {/* Left Side — Quick Actions + Appointments */}
                <Box sx={leftColumnStyles}>
                    {/* Quick Actions */}
                    <Card sx={cardMarginBottom}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={sectionTitleStyles}>
                                {translate(ResourcesIds.QUICK_ACTIONS)}
                            </Typography>

                            <Box sx={quickActionsGridStyles}>
                                {QUICK_ACTIONS.map((action, index) => (
                                    <Card
                                        key={index}
                                        sx={quickActionCardStyles(theme, (theme.palette as any)[action.colorKey].main)}
                                        onClick={() => navigate(action.route)}
                                    >
                                        <Box sx={quickActionInnerStyles}>
                                            <Box sx={quickActionIconTileStyles(index === 0 ? 'primary' : 'secondary')}>
                                                {index === 0 ? (
                                                    <CalendarToday sx={quickActionIconStyle(theme, 'primary')} />
                                                ) : (
                                                    <ViewList sx={quickActionIconStyle(theme, 'secondary')} />
                                                )}
                                            </Box>

                                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                {action.title}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">
                                            {action.description}
                                        </Typography>
                                    </Card>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Active Plans */}
                    <Card sx={cardMarginBottom}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={sectionTitleStyles}>
                                {translate(ResourcesIds.DASHBOARD_ACTIVE_PLANS_TITLE)}
                            </Typography>

                            {isLoadingPurchases ? (
                                <Box sx={loadingBoxStyles}>
                                    <CircularProgress size={32} />
                                </Box>
                            ) : activePlans.length === 0 ? (
                                <Box sx={emptyStateBoxStyles}>
                                    <Inventory2Outlined sx={planIconStyles(theme)} />
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        {translate(ResourcesIds.DASHBOARD_ACTIVE_PLANS_EMPTY_TITLE)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {translate(ResourcesIds.DASHBOARD_ACTIVE_PLANS_EMPTY_SUBTITLE)}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={activePlansListStyles}>
                                    {activePlans.map(plan => (
                                        <Box key={plan.id} sx={activePlanRowStyles}>
                                            <Box sx={activePlanHeaderRowStyles}>
                                                <Typography sx={activePlanServiceNameStyles}>
                                                    {localised(plan.serviceNameRo, plan.serviceNameEn, i18n.language)}
                                                </Typography>
                                                {plan.therapistName && (
                                                    <Typography sx={activePlanTherapistStyles}>
                                                        {plan.therapistName}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {plan.serviceType === 'PACKAGE' ? (
                                                <>
                                                    <Typography sx={activePlanRemainingStyles}>
                                                        {translate(ResourcesIds.PURCHASE_SESSIONS_REMAINING, {
                                                            remaining: plan.remainingSessions,
                                                            total: plan.totalSessions,
                                                        })}
                                                    </Typography>
                                                    {plan.scheduledSessions > 0 && (
                                                        <Typography sx={activePlanScheduledStyles}>
                                                            {translate(ResourcesIds.PURCHASE_SESSIONS_BOOKED, {
                                                                booked: plan.scheduledSessions,
                                                            })}
                                                        </Typography>
                                                    )}
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={(sessionsUsed(plan) / plan.totalSessions) * 100}
                                                        sx={activePlanProgressBarStyles}
                                                    />
                                                </>
                                            ) : (
                                                <Chip
                                                    size="small"
                                                    sx={activePlanChipStyles}
                                                    color={sessionsUsed(plan) > 0 ? 'success' : 'default'}
                                                    variant={sessionsUsed(plan) > 0 ? 'filled' : 'outlined'}
                                                    label={translate(
                                                        sessionsUsed(plan) > 0
                                                            ? ResourcesIds.PURCHASE_SESSION_HELD
                                                            : ResourcesIds.PURCHASE_SESSION_NOT_HELD
                                                    )}
                                                />
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Upcoming Appointments */}
                    <Card sx={cardMarginBottom}>
                        <CardContent>
                            <Box sx={upcomingHeaderBoxStyles}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate(ResourcesIds.UPCOMING_TITLE)}
                                </Typography>
                                {upcomingTotal > UPCOMING_LIMIT && (
                                    <Button variant="outlined" size="small" onClick={() => navigate(myAppointmentsLink)}>
                                        {translate(ResourcesIds.VIEW_ALL_BUTTON)}
                                    </Button>
                                )}
                            </Box>

                            {isLoading ? (
                                <Box sx={loadingBoxStyles}>
                                    <CircularProgress size={32} />
                                </Box>
                            ) : upcomingAppointments.length === 0 ? (
                                <Box sx={emptyStateBoxStyles}>
                                    <CalendarToday sx={calendarIconStyles(theme)} />
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        {translate(ResourcesIds.EMPTY_STATE_TITLE)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {translate(ResourcesIds.EMPTY_STATE_SUBTITLE)}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={upcomingListStyles}>
                                    {upcomingAppointments.map(appointment => (
                                        <Box key={appointment.id} sx={upcomingRowStyles}>
                                            <Box>
                                                <Typography sx={upcomingWhenStyles}>
                                                    {formatRecordDateTime(appointment.startsAt, i18n)}
                                                    {appointment.status === 'REQUESTED' && (
                                                        <Chip
                                                            size="small"
                                                            color="info"
                                                            sx={upcomingStatusChipStyles}
                                                            label={translate(ResourcesIds.APPOINTMENT_STATUS_REQUESTED)}
                                                        />
                                                    )}
                                                </Typography>
                                                <Typography sx={upcomingWithStyles}>
                                                    {[appointment.serviceNameRo
                                                        && localised(appointment.serviceNameRo, appointment.serviceNameEn, i18n.language),
                                                    appointment.therapistName]
                                                        .filter(Boolean)
                                                        .join(' · ')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </>
    );
};